import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Download } from 'lucide-react';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MarcoFichaje } from '@/components/MarcoFichaje';
import { DialogoCorreccion } from '@/components/fichajes/DialogoCorreccion';
import {
  fechaCorta, hoyEn, horaLocal, horasMinutos, NOMBRES_TIPO, resumenPropuesta,
} from '@/lib/fichajes';

/**
 * Mis registros: el mes de quien mira, lo corregido marcado con su motivo, el
 * mismo PDF que le llega por email el día 1, y la forma de pedir que le
 * corrijan algo. Pedir no cambia el registro: lo hace el admin al aprobarlo.
 */

const ESTADO = {
  pendiente: { texto: 'Pendiente', clase: 'bg-sky-100 text-sky-800' },
  aprobada: { texto: 'Aprobada', clase: 'bg-green-100 text-green-800' },
  rechazada: { texto: 'Rechazada', clase: 'bg-red-100 text-red-800' },
};
const DECISION = { pago: 'se pagan', descanso: 'se compensan con descanso' };

function observacion(dia) {
  const notas = [];
  if (dia.estado === 'incompleta') notas.push('Sin salida');
  if (dia.estado === 'abierta') notas.push('En curso');
  if (dia.corregida) notas.push('Corregida');
  return notas.join(' · ');
}

export default function MisRegistros({ user }) {
  const [mes, setMes] = useState('');
  const [datos, setDatos] = useState(null);
  const [solicitudes, setSolicitudes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [peticion, setPeticion] = useState(null);

  const cargar = useCallback(async (mesPedido) => {
    setCargando(true);
    try {
      const [r, s] = await Promise.all([
        axiosInstance.get('/fichajes/mios', { params: mesPedido ? { mes: mesPedido } : {} }),
        axiosInstance.get('/fichajes/solicitudes'),
      ]);
      setDatos(r.data);
      setMes(r.data.resumen.mes);
      setSolicitudes(s.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se han podido cargar tus registros');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(null); }, [cargar]);

  const descargarPdf = async () => {
    try {
      const r = await axiosInstance.get('/fichajes/mios/pdf', { params: { mes }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `registro-jornada-${mes}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('No se ha podido descargar el PDF');
    }
  };

  const resumen = datos?.resumen;
  const zona = datos?.zona_horaria || 'Europe/Madrid';
  const correcciones = resumen
    ? resumen.dias.flatMap((d) => d.correcciones.map((c) => ({ ...c, fecha: d.fecha })))
    : [];
  const pendientes = new Set(solicitudes.filter((s) => s.estado === 'pendiente').map((s) => s.fecha));

  const pedirPara = (d) => setPeticion({
    fecha: d.fecha,
    jornadas: [{ jornada_id: d.jornada_id, fecha_local: d.fecha, entrada: d.entrada, eventos: d.eventos }],
  });

  return (
    <MarcoFichaje user={user}>
      <div className="space-y-5">
        <div className="flex items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Mis registros</h1>
            <p className="text-sm text-slate-500">Tu registro de jornada, mes a mes</p>
          </div>
          <div>
            <Label htmlFor="mes" className="text-xs text-slate-500">Mes</Label>
            <Input
              id="mes"
              type="month"
              value={mes}
              onChange={(e) => e.target.value && cargar(e.target.value)}
              className="w-40"
            />
          </div>
        </div>

        {cargando && !resumen ? (
          <p className="text-slate-500">Cargando…</p>
        ) : resumen && (
          <>
            {/* index.css fuerza grid-cols-3 a una columna en el móvil: flex no le afecta. */}
            <div className="flex gap-3">
              <Card className="flex-1 min-w-0"><CardContent className="pt-4 px-3">
                <p className="text-xs text-slate-500">Horas trabajadas</p>
                <p className="text-xl font-bold tabular-nums">{horasMinutos(resumen.total_minutos)}</p>
              </CardContent></Card>
              <Card className="flex-1 min-w-0"><CardContent className="pt-4 px-3">
                <p className="text-xs text-slate-500">Sin salida</p>
                <p className={`text-xl font-bold ${resumen.jornadas_incompletas ? 'text-amber-700' : ''}`}>
                  {resumen.jornadas_incompletas}
                </p>
              </CardContent></Card>
              <Card className="flex-1 min-w-0"><CardContent className="pt-4 px-3">
                <p className="text-xs text-slate-500">{resumen.etiqueta_exceso}*</p>
                <p className="text-xl font-bold tabular-nums">{horasMinutos(resumen.exceso_minutos)}</p>
              </CardContent></Card>
            </div>

            {resumen.decision_exceso && (
              <p className="text-sm text-slate-700">
                La empresa ha decidido que esas {horasMinutos(resumen.decision_exceso.minutos)} h{' '}
                {DECISION[resumen.decision_exceso.compensacion]}.
              </p>
            )}

            <Card>
              <CardContent className="pt-4 overflow-x-auto">
                {resumen.dias.length === 0 ? (
                  <p className="text-sm text-slate-500 py-4 text-center">No hay jornadas registradas este mes.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-slate-500 border-b">
                        <th className="py-2 pr-2 font-medium">Día</th>
                        <th className="py-2 pr-2 font-medium">Entrada</th>
                        <th className="py-2 pr-2 font-medium">Salida</th>
                        <th className="py-2 pr-2 font-medium">Pausas</th>
                        <th className="py-2 pr-2 font-medium">Horas</th>
                        <th className="py-2 font-medium" />
                      </tr>
                    </thead>
                    <tbody className="tabular-nums">
                      {resumen.dias.map((d) => (
                        <tr key={d.jornada_id} className="border-b last:border-0 align-top">
                          <td className="py-2 pr-2">{fechaCorta(d.fecha)}</td>
                          <td className="py-2 pr-2">{horaLocal(d.entrada, zona)}</td>
                          <td className="py-2 pr-2">{d.salida ? horaLocal(d.salida, zona) : '-'}</td>
                          <td className="py-2 pr-2">{d.minutos_pausa ? horasMinutos(d.minutos_pausa) : '-'}</td>
                          <td className="py-2 pr-2 font-semibold">{horasMinutos(d.minutos)}</td>
                          <td className="py-2 text-xs">
                            <span className={`block ${d.estado === 'incompleta' ? 'text-amber-700' : 'text-slate-500'}`}>
                              {observacion(d)}
                            </span>
                            {pendientes.has(d.fecha) ? (
                              <span className="text-sky-700">Corrección pedida</span>
                            ) : (
                              <button type="button" onClick={() => pedirPara(d)} className="text-slate-600 underline">
                                Pedir corrección
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>

            <Button variant="outline" className="w-full"
              onClick={() => setPeticion({ fecha: hoyEn(zona), jornadas: [] })}>
              Me falta un día entero: pedir que lo añadan
            </Button>

            {correcciones.length > 0 && (
              <Card>
                <CardContent className="pt-4 space-y-2 text-sm">
                  <p className="font-semibold text-slate-900">Correcciones</p>
                  {correcciones.map((c, i) => (
                    <p key={i} className="text-slate-700">
                      {fechaCorta(c.fecha)} · {NOMBRES_TIPO[c.tipo] || c.tipo}
                      {c.tipo !== 'anulacion' && ` a las ${horaLocal(c.ts, zona)}`} · «{c.motivo}»
                    </p>
                  ))}
                </CardContent>
              </Card>
            )}

            {solicitudes.length > 0 && (
              <Card>
                <CardContent className="pt-4 space-y-2 text-sm">
                  <p className="font-semibold text-slate-900">Mis solicitudes</p>
                  {solicitudes.slice(0, 10).map((s) => (
                    <div key={s.id} className="border-b last:border-0 pb-2">
                      <div className="flex items-center justify-between gap-2">
                        <span>{fechaCorta(s.fecha)} · {resumenPropuesta(s)}</span>
                        <span className={`text-xs rounded px-2 py-0.5 ${ESTADO[s.estado]?.clase || ''}`}>
                          {ESTADO[s.estado]?.texto || s.estado}
                        </span>
                      </div>
                      {s.respuesta && <p className="text-slate-600">Respuesta: «{s.respuesta}»</p>}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            <Button variant="outline" onClick={descargarPdf} className="w-full">
              <Download className="w-4 h-4 mr-2" /> Descargar el PDF de este mes
            </Button>

            <p className="text-xs text-slate-500">
              * Orientativo: compara lo trabajado con tu jornada pactada ({resumen.jornada_semanal_horas} h
              a la semana) y no descuenta festivos ni vacaciones.
            </p>
          </>
        )}
      </div>

      <DialogoCorreccion
        modo="pedir"
        abierto={!!peticion}
        alCerrar={() => setPeticion(null)}
        fecha={peticion?.fecha}
        jornadas={peticion?.jornadas || []}
        zona={zona}
        alGuardar={() => { setPeticion(null); cargar(mes); }}
      />
    </MarcoFichaje>
  );
}
